import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Gift, ChevronRight, ChevronLeft, Check, Euro, User, Mail,
  MapPin, Loader2, AlertCircle, MessageSquare
} from 'lucide-react';
import { GiftCardVisual } from './GiftCardsPage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AMOUNTS = [25, 50, 75, 100, 150, 200];
const DESIGNS = [
  { id: 'classic', label: 'Classico', emoji: '' },
  { id: 'birthday', label: 'Compleanno', emoji: '🎂' },
  { id: 'holiday', label: 'Festività', emoji: '🎄' },
  { id: 'thank_you', label: 'Ringraziamento', emoji: '💐' },
];

const PublicGiftCardPage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salonData, setSalonData] = useState(null);
  const [step, setStep] = useState(0); // 0=amount, 1=personalize, 2=info, 3=confirm
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [design, setDesign] = useState('classic');
  const [message, setMessage] = useState('');
  const [senderName, setSenderName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');

  const primaryColor = salonData?.salon?.color || '#14B8A6';
  const finalAmount = selectedAmount === 'custom' ? parseFloat(customAmount) || 0 : selectedAmount;

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/giftcard/${slug}`);
        setSalonData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Salone non trovato' : 'Errore nel caricamento');
      } finally { setLoading(false); }
    };
    fetch();
  }, [slug]);

  const handlePurchase = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/giftcard/${slug}/purchase`, {
        value: finalAmount, design, message, color: primaryColor,
        sender_name: senderName, recipient_name: recipientName, recipient_email: recipientEmail,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nell\'acquisto');
    } finally { setSubmitting(false); }
  };

  const canNext = () => {
    switch (step) {
      case 0: return finalAmount > 0;
      case 1: return true;
      case 2: return senderName.trim() && recipientName.trim();
      default: return false;
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="text-center">
      <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" /><h1 className="text-xl font-semibold mb-2">{error}</h1>
    </div></div>
  );

  if (result) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }}>
      <Card className="w-full max-w-md shadow-lg" data-testid="purchase-confirmed">
        <CardContent className="p-8 text-center space-y-5">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
            <Check className="w-8 h-8" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-2xl font-bold">Gift Card Creata!</h2>
          <GiftCardVisual value={finalAmount} color={primaryColor} recipient={recipientName} message={message} design={design} code={result.code} salonName={result.salon_name} />
          <div className="p-3 rounded-lg bg-muted text-center">
            <p className="text-xs text-muted-foreground">Codice Gift Card</p>
            <p className="text-2xl font-mono font-bold tracking-widest mt-1">{result.code}</p>
          </div>
          {recipientEmail && <p className="text-xs text-muted-foreground">Un'email con il codice è stata inviata a {recipientEmail}</p>}
          <p className="text-xs text-muted-foreground">Presenta questo codice in salone per utilizzare la Gift Card.</p>
        </CardContent>
      </Card>
    </div>
  );

  const salon = salonData.salon;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${primaryColor}08 0%, #f9fafb 40%)` }} data-testid="public-giftcard-page">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          {salon.logo_url && <img src={`${API.replace('/api', '')}${salon.logo_url}`} alt="" className="w-10 h-10 rounded-lg object-contain" />}
          <div>
            <h1 className="font-bold text-lg">{salon.name}</h1>
            {salon.city && <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{salon.city}</p>}
          </div>
          <Gift className="w-5 h-5 ml-auto" style={{ color: primaryColor }} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Progress */}
        <div className="flex items-center gap-1 mb-6">
          {['Importo', 'Personalizza', 'Dettagli', 'Conferma'].map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center transition-all ${i <= step ? 'text-white' : 'bg-gray-200 text-gray-500'}`} style={i <= step ? { backgroundColor: primaryColor } : {}}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < 3 && <div className="flex-1 h-0.5 mx-1" style={{ backgroundColor: i < step ? primaryColor : '#e5e7eb' }} />}
            </div>
          ))}
        </div>

        {/* Step 0: Amount */}
        {step === 0 && (
          <div className="space-y-4" data-testid="step-amount">
            <h2 className="text-lg font-semibold">Scegli l'importo</h2>
            <div className="grid grid-cols-3 gap-3">
              {AMOUNTS.map(amt => (
                <button key={amt} type="button" onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                  className={`py-4 rounded-xl border text-center font-bold text-lg transition-all ${selectedAmount === amt ? 'text-white shadow-md' : 'bg-white hover:border-gray-300'}`}
                  style={selectedAmount === amt ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
                  data-testid={`amount-${amt}`}
                >€{amt}</button>
              ))}
            </div>
            <div className="text-center text-sm text-muted-foreground">oppure</div>
            <div className="space-y-2">
              <Label>Importo personalizzato</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="number" min="5" step="1" placeholder="Inserisci importo" className="pl-9 text-lg"
                  value={customAmount} onChange={e => { setCustomAmount(e.target.value); setSelectedAmount('custom'); }} data-testid="custom-amount" />
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Personalize */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-personalize">
            <h2 className="text-lg font-semibold">Personalizza</h2>
            <div className="space-y-2">
              <Label>Design</Label>
              <div className="grid grid-cols-4 gap-2">
                {DESIGNS.map(d => (
                  <button key={d.id} type="button" onClick={() => setDesign(d.id)}
                    className={`p-3 rounded-xl border text-center transition-all ${design === d.id ? 'ring-2 bg-white shadow-sm' : 'bg-white hover:border-gray-300'}`}
                    style={design === d.id ? { borderColor: primaryColor, ringColor: primaryColor } : {}}
                    data-testid={`design-${d.id}`}
                  >
                    <span className="text-lg">{d.emoji || '🎁'}</span>
                    <p className="text-[10px] mt-0.5">{d.label}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Messaggio dedicato (opzionale)</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Buon compleanno! Goditi un trattamento speciale..." rows={2} data-testid="gc-pub-message" />
            </div>
            <GiftCardVisual value={finalAmount} color={primaryColor} recipient={recipientName || 'Destinatario'} message={message} design={design} code="GC-XXXX-XXXX" salonName={salon.name} />
          </div>
        )}

        {/* Step 2: Info */}
        {step === 2 && (
          <div className="space-y-4 bg-white rounded-xl border p-5" data-testid="step-info">
            <h2 className="text-lg font-semibold">I tuoi dati</h2>
            <div className="space-y-2">
              <Label>Il tuo nome *</Label>
              <Input value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="Il tuo nome" data-testid="gc-sender-name" />
            </div>
            <div className="space-y-2">
              <Label>Nome destinatario *</Label>
              <Input value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="A chi è destinata" data-testid="gc-recipient-name" />
            </div>
            <div className="space-y-2">
              <Label>Email destinatario (opzionale)</Label>
              <Input type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="email@esempio.com — riceverà il codice" data-testid="gc-recipient-email" />
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-confirm">
            <h2 className="text-lg font-semibold">Riepilogo</h2>
            <GiftCardVisual value={finalAmount} color={primaryColor} recipient={recipientName} message={message} design={design} code="GC-XXXX-XXXX" salonName={salon.name} />
            <div className="bg-white rounded-xl border p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Importo</span><span className="font-bold">€{finalAmount.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Da</span><span>{senderName}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Per</span><span>{recipientName}</span></div>
              {recipientEmail && <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{recipientEmail}</span></div>}
              {message && <div className="pt-2 border-t"><p className="text-muted-foreground text-xs">Messaggio:</p><p className="italic">"{message}"</p></div>}
            </div>
            <p className="text-xs text-center text-muted-foreground">
              Pagamento sicuro con carta. Il codice arriva via email subito dopo l'incasso.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 pb-8">
          {step > 0 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="gc-btn-back"><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
          ) : <div />}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()} style={{ backgroundColor: canNext() ? primaryColor : undefined }} data-testid="gc-btn-next">
              Avanti<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handlePurchase} disabled={submitting} style={{ backgroundColor: primaryColor }} data-testid="gc-btn-confirm">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
              Paga €{finalAmount.toFixed(2)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicGiftCardPage;
